import * as Sentry from '@sentry/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import claimFreeLogic from '../contracts/utils'
import useL1NS from './useL1NS'

interface RetvalProps {
  code: string
  fullHandle: string
  nonceHex: string
}

const useClaimFreeHandle = (address: string, handle: string) => {
  const [retval, setRetval] = useState<RetvalProps>({
    code: '',
    fullHandle: '',
    nonceHex: '',
  })
  const hasFetched = useRef(false)

  const { confirmHandleAvailability, getDifficulty } = useL1NS()
  const [errorPow, setErrorPow] = useState<string>('')

  const executePow = async (): Promise<boolean> => {
    try {
      // Read difficulty from contract
      const difficulty = await getDifficulty()

      // Calculate nonce and Execute PoW with given difficulty
      const res = await claimFreeLogic(address, handle, difficulty)
      setRetval(res)

      // Check if handle is available
      let available = await confirmHandleAvailability(res.fullHandle)

      // If handle is not available, recompute PoW
      if (!available) {
        available = await executePow()
      }
      return available
    } catch (e: any) {
      console.error('Error:', e)
      Sentry.withScope(scope => {
        scope.setTag('free_username', 'pow')
      })
      setErrorPow(`Error executing PoW: ${e.message}`)
      throw new Error(`Error executing PoW: ${e.message}`)
    }
  }

  useEffect(() => {
    if (!hasFetched.current && address) {
      executePow()
      hasFetched.current = true
    }
  }, [handle, address])

  const refetch = useCallback(async () => {
    if (address) {
      await executePow()
      hasFetched.current = true
    }
  }, [])

  return { retval, refetch, errorPow }
}

export default useClaimFreeHandle
